/**
 * CompanyDetailPage — company detail (yritysrekisteri, #763/#764)
 *
 * Shows basic info, billing details, contact persons (person-company links),
 * the vehicles the company is an active party to and the work orders it has
 * ordered (#2519). Only the company's own links count: a car that belongs to a
 * contact person privately is on that person's card, not here.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  useCompany,
  useDeleteCompany,
} from '@/hooks/useCompanies';
import { AttachmentsCard } from '@/components/attachments/AttachmentsCard';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { PartyVehiclesSection } from '@/components/parties/PartyVehiclesSection';
import { PartyWorkOrdersSection } from '@/components/parties/PartyWorkOrdersSection';
import { PersonCompanyLinksSection } from '@/components/persons/PersonCompanyLinksSection';
import {
  ConfirmDialog,
  DetailField,
  DetailPageHeader,
  DetailSection,
  PermissionGuard,
} from '@/components/common';
import { useNotify } from '@/stores/notificationStore';

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('companies');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();

  const { data: company, isLoading } = useCompany(id);
  const deleteMutation = useDeleteCompany();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!company) {
    return <Typography color="text.secondary">{t('empty')}</Typography>;
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(company.id);
      notify.success(t('deleted'), '');
      navigate('/parties?tab=companies');
    } catch {
      notify.error(tc('error'), '');
    }
  };

  const addr = [company.streetAddress, [company.postalCode, company.city].filter(Boolean).join(' ')]
    .filter((part) => part && part.length > 0)
    .join(', ');
  const billingAddr = [
    company.billingStreetAddress,
    [company.billingPostalCode, company.billingCity].filter(Boolean).join(' '),
  ]
    .filter((part) => part && part.length > 0)
    .join(', ');

  return (
    <Box>
      <DetailPageHeader
        title={company.name}
        onBack={() => navigate('/parties?tab=companies')}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setConfirmDelete(true)}
      />

      <Stack spacing={3}>
        <DetailSection title={t('sections.basicInfo')}>
          <Stack spacing={2}>
            <DetailField label={t('fields.businessId')} value={company.businessId} />
            <DetailField label={t('fields.phone')} value={company.phone} />
            <DetailField label={t('fields.email')} value={company.email} />
            <DetailField label={t('fields.website')} value={company.website} />
            <DetailField label={t('sections.address')} value={addr || null} />
            <DetailField label={t('fields.notes')} value={company.notes} />
          </Stack>
        </DetailSection>

        <DetailSection title={t('billing.title')}>
          <Stack spacing={2}>
            <DetailField label={t('billing.billingStreetAddress')} value={billingAddr || null} />
            <DetailField label={t('billing.invoiceEmail')} value={company.invoiceEmail} />
            <DetailField label={t('billing.eInvoiceAddress')} value={company.eInvoiceAddress} />
            <DetailField label={t('billing.ovtId')} value={company.ovtId} />
            <DetailField label={t('billing.eInvoiceOperator')} value={company.eInvoiceOperator ?? null} />
            <DetailField
              label={t('billing.paymentTermsDays')}
              value={company.paymentTermsDays != null ? String(company.paymentTermsDays) : null}
            />
          </Stack>
        </DetailSection>

        <PersonCompanyLinksSection companyId={company.id} />

        <PermissionGuard permission="vehicles.read">
          <PartyVehiclesSection companyId={company.id} />
        </PermissionGuard>

        <PermissionGuard permission="work_orders.read">
          <PartyWorkOrdersSection companyId={company.id} />
        </PermissionGuard>

        <AttachmentsCard
          entityLinks={[{ entityType: 'company', entityId: company.id }]}
          canModify={() => true}
          defaultTags={[company.name]}
        />
      </Stack>

      <CompanyFormDialog open={editOpen} company={company} onClose={() => setEditOpen(false)} />
      <ConfirmDialog
        open={confirmDelete}
        title={t('deleteConfirmTitle')}
        message={t('deleteConfirmMessage', { name: company.name })}
        confirmText={tc('delete')}
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}
